import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ServiceModal from '../home/components/ServiceModal';
import Footer from '../home/components/Footer';

export type ServiceType = 'handyman' | 'plumbing' | 'electrical' | 'waste' | 'transportation' | 'cleaning';

export default function ServicesPage() {
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Our Services - Taskbudy Professional Home Services';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Browse all professional home services offered by Taskbudy including handyman services, plumbing repairs, electrical installations, and waste removal. Book trusted experts today.');
    }

    // Add Schema.org JSON-LD for services page
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Taskbudy Services",
      "description": "Professional home services offered by Taskbudy",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Service",
            "name": "Handyman Services",
            "description": "General repairs, furniture assembly, painting, and more",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Taskbudy"
            },
            "offers": {
              "@type": "Offer",
              "price": "40",
              "priceCurrency": "GBP",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "40",
                "priceCurrency": "GBP",
                "unitText": "hour"
              }
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Service",
            "name": "Plumbing Services",
            "description": "Leak repairs, installations, emergency plumbing",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Taskbudy"
            },
            "offers": {
              "@type": "Offer",
              "price": "75",
              "priceCurrency": "GBP",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "75",
                "priceCurrency": "GBP",
                "unitText": "hour"
              }
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Service",
            "name": "Electrical Services",
            "description": "Wiring, installations, safety inspections",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Taskbudy"
            },
            "offers": {
              "@type": "Offer",
              "price": "70",
              "priceCurrency": "GBP",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "70",
                "priceCurrency": "GBP",
                "unitText": "hour"
              }
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "Service",
            "name": "Waste Removal",
            "description": "Rubbish clearance, skip hire, recycling",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Taskbudy"
            },
            "offers": {
              "@type": "Offer",
              "price": "350",
              "priceCurrency": "GBP"
            }
          }
        }
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      if (schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, []);

  const handleServiceSelect = (service: ServiceType) => {
    const isLoggedIn = localStorage.getItem('user_token');
    
    if (!isLoggedIn) {
      setShowAuthPrompt(true);
      setSelectedService(service);
    } else {
      setSelectedService(service);
    }
  };

  const services = [
    {
      id: 'handyman',
      name: 'Handyman Services',
      icon: 'ri-tools-fill',
      color: 'from-[#0B3C5D] to-[#0B3C5D]/80',
      hoverColor: 'hover:from-[#082A42] hover:to-[#082A42]/80',
      description: 'General repairs, furniture assembly, painting, and more',
      price: 'From £45/hour',
      features: [
        'Furniture Assembly',
        'Picture Hanging',
        'Door Repairs',
        'Painting & Decorating',
        'General Repairs',
        'Home Maintenance',
        'Shelf Installation',
        'Curtain & Blind Fitting',
        'Flat Pack Assembly',
        'Minor Carpentry',
        'Sealant & Grouting',
        'Lock Repairs'
      ],
      image: 'https://public.readdy.ai/ai/img_res/7f4d8c8d64317e68b935b0aec2a061a0.jpg'
    },
    {
      id: 'plumbing',
      name: 'Plumbing Services',
      icon: 'ri-drop-fill',
      color: 'from-[#0B3C5D] to-[#0B3C5D]/80',
      hoverColor: 'hover:from-[#082A42] hover:to-[#082A42]/80',
      description: 'Leak repairs, installations, emergency plumbing',
      price: 'From £80/hour',
      features: [
        'Leak Detection & Repair',
        'Pipe Installation',
        'Drain Cleaning',
        'Toilet & Sink Repairs',
        'Water Heater Service',
        'Emergency Plumbing',
        'Tap Installation',
        'Shower Installation',
        'Radiator Repairs',
        'Boiler Repairs',
        'Bathroom Fitting',
        'Kitchen Plumbing'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20plumber%20repairing%20pipes%20and%20fixtures%20in%20modern%20bathroom%2C%20working%20with%20plumbing%20tools%20and%20equipment%2C%20clean%20professional%20setting%2C%20simple%20white%20background%20highlighting%20plumbing%20work%2C%20bright%20lighting&width=800&height=600&seq=plumbing-service-001&orientation=landscape'
    },
    {
      id: 'electrical',
      name: 'Electrical Services',
      icon: 'ri-flashlight-fill',
      color: 'from-[#0B3C5D] to-[#0B3C5D]/80',
      hoverColor: 'hover:from-[#082A42] hover:to-[#082A42]/80',
      description: 'Wiring, installations, safety inspections',
      price: 'From £75/hour',
      features: [
        'Electrical Installations',
        'Lighting Fixtures',
        'Socket & Switch Repairs',
        'Safety Inspections',
        'Fuse Box Upgrades',
        'Emergency Electrical',
        'Rewiring',
        'Appliance Installation',
        'Outdoor Lighting',
        'Security Lighting',
        'Electric Shower Installation',
        'PAT Testing'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20electrician%20installing%20electrical%20fixtures%20and%20wiring%20in%20modern%20home%2C%20working%20with%20electrical%20tools%20and%20testing%20equipment%2C%20clean%20professional%20workspace%2C%20simple%20white%20background%20highlighting%20electrical%20work&width=800&height=600&seq=electrical-service-001&orientation=landscape'
    },
    {
      id: 'waste',
      name: 'Waste Removal',
      icon: 'ri-delete-bin-fill',
      color: 'from-[#0B3C5D] to-[#0B3C5D]/80',
      hoverColor: 'hover:from-[#082A42] hover:to-[#082A42]/80',
      description: 'Rubbish clearance, skip hire, recycling',
      price: 'From £90',
      features: [
        'House Clearance',
        'Garden Waste Removal',
        'Half Load - £180',
        'Full Load - £360',
        'Recycling Services',
        'Same-Day Collection',
        'Office Clearance',
        'Furniture Disposal',
        'Appliance Removal',
        'Construction Waste',
        'Green Waste',
        'Commercial Waste'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20waste%20removal%20service%20with%20workers%20loading%20rubbish%20into%20truck%2C%20clean%20organized%20waste%20collection%2C%20modern%20waste%20management%20vehicle%2C%20simple%20white%20background%20highlighting%20the%20service%2C%20professional%20atmosphere&width=800&height=600&seq=waste-service-001&orientation=landscape'
    },
    {
      id: 'transportation',
      name: 'Transportation & Moving',
      icon: 'ri-truck-fill',
      color: 'from-[#0B3C5D] to-[#0B3C5D]/80',
      hoverColor: 'hover:from-[#082A42] hover:to-[#082A42]/80',
      description: 'Professional moving and delivery services',
      price: '£50 + £1.50/mile',
      features: [
        'Home & Office Moving',
        'Furniture Delivery',
        'Packing Services',
        'Loading & Unloading',
        'Long Distance Moving',
        'Base Fee: £50 + £1.50/mile',
        'Man with Van',
        'Single Item Delivery',
        'Store Collections',
        'Ikea Delivery',
        'Appliance Transport',
        'Student Moves'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20movers%20loading%20furniture%20and%20boxes%20into%20moving%20truck%2C%20organized%20transportation%20service%20with%20modern%20van%2C%20workers%20in%20uniform%2C%20simple%20white%20background%20highlighting%20moving%20service%2C%20professional%20atmosphere&width=800&height=600&seq=transportation-service-001&orientation=landscape'
    },
    {
      id: 'cleaning',
      name: 'Cleaning Services',
      icon: 'ri-brush-fill',
      color: 'from-[#0B3C5D] to-[#0B3C5D]/80',
      hoverColor: 'hover:from-[#082A42] hover:to-[#082A42]/80',
      description: 'Deep cleaning and regular maintenance',
      price: 'From £30/hour',
      features: [
        'Deep Cleaning',
        'Regular Maintenance',
        'End of Tenancy Cleaning',
        'Carpet Cleaning',
        'Window Cleaning',
        'Office Cleaning',
        'After Builders Cleaning',
        'Oven Cleaning',
        'Upholstery Cleaning',
        'Spring Cleaning',
        'Move In/Out Cleaning',
        'One-Off Cleaning'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20cleaner%20cleaning%20modern%20home%20interior%20with%20cleaning%20supplies%20and%20equipment%2C%20bright%20clean%20room%20with%20organized%20workspace%2C%20cleaning%20products%20visible%2C%20simple%20white%20background%20highlighting%20cleaning%20service%2C%20professional%20atmosphere&width=800&height=600&seq=cleaning-service-001&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white w-full">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-md`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/e0dadf219d3b341a9184e92001cfe9b6.png" 
                alt="Taskbudy" 
                className="h-8 lg:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Home
              </Link>
              <Link to="/services" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Services
              </Link>
              <Link to="/locations" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Locations
              </Link>
              <Link to="/pricing" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Pricing
              </Link>
              <Link to="/about" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                About
              </Link>
              <Link to="/contact" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Contact
              </Link>
              <Link to="/auth/signin" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Sign In
              </Link>
              <Link to="/auth/signup" className="text-[#0F2A44] hover:text-[#fe7a14] font-medium transition-colors whitespace-nowrap">
                Sign Up
              </Link>
              <Link
                to="/provider-signup"
                className="bg-[#fe7a14] hover:bg-[#e56a0f] text-white px-6 py-3 rounded-xl font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Become a Provider
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#0F2A44] w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="fixed top-[58px] left-0 right-0 bg-white shadow-lg z-40 border-t border-gray-200 animate-slideDown">
              <div className="flex flex-col py-2 max-h-[calc(100vh-58px)] overflow-y-auto">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Home
                </Link>
                <Link
                  to="/services"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Services
                </Link>
                <Link
                  to="/locations"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Locations
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Pricing
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Contact
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/auth/signin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth/signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-transparent font-medium transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                >
                  Sign Up
                </Link>
                <Link
                  to="/provider-signup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mx-4 my-2 px-4 py-3 bg-gradient-to-r from-[#fe7a14] to-[#ff8c3a] text-white font-bold rounded-lg text-center hover:from-[#e56a0f] hover:to-[#fe7a14] transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105 shadow-md"
                >
                  Become a Provider
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20">
        {/* Back Button - Top Left */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors mb-8 group"
        >
          <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
          <span className="font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Professional tradespeople ready to help with all your home and business needs
          </p>
        </div>

        {/* Hero Section */}
        <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-[#0B3C5D]/10 to-[#FF6A00]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] mb-4 animate-[fadeInUp_0.8s_ease-out]">
                Professional Home Services
              </h1>
              <p className="text-base sm:text-lg text-gray-600 animate-[fadeInUp_1s_ease-out]">
                Choose from our range of trusted professional services. All providers are verified, insured, and ready to help.
              </p>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {services.map((service, index) => (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2 animate-[fadeInUp_0.8s_ease-out]"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => handleServiceSelect(service.id as ServiceType)}
                >
                  {/* Image */}
                  <div className="relative h-48 sm:h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}></div>
                    
                    {/* Icon Badge */}
                    <div className={`absolute top-4 right-4 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${service.color} rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-300`}>
                      <i className={`${service.icon} text-white text-xl sm:text-2xl`}></i>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start mb-3 gap-2">
                      <h3 className="text-xl sm:text-2xl font-bold text-[#0B3C5D] group-hover:text-[#FF6A00] transition-colors duration-300">
                        {service.name}
                      </h3>
                      <span className="text-base sm:text-lg font-bold text-[#FF6A00] whitespace-nowrap">
                        {service.price}
                      </span>
                    </div>
                    
                    <p className="text-sm sm:text-base text-gray-600 mb-4">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                          <i className="ri-check-line text-[#FF6A00] text-base flex-shrink-0"></i>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Book Button */}
                    <button
                      className={`w-full py-3 bg-gradient-to-r ${service.color} ${service.hoverColor} text-white rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer transform group-hover:scale-105 shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base`}
                    >
                      Book {service.name}
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: 'ri-shield-check-fill', text: 'Verified Professionals', color: 'text-[#FF6A00]' },
                { icon: 'ri-time-fill', text: 'Same-Day Service', color: 'text-[#0B3C5D]' },
                { icon: 'ri-money-pound-circle-fill', text: 'Transparent Pricing', color: 'text-[#FF6A00]' },
                { icon: 'ri-customer-service-2-fill', text: '24/7 Support', color: 'text-[#0B3C5D]' }
              ].map((item, index) => (
                <div key={index} className="text-center group cursor-pointer animate-[fadeInUp_1s_ease-out]" style={{ animationDelay: `${0.4 + index * 0.1}s` }}>
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 ${item.color} bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${item.icon} text-xl sm:text-2xl`}></i>
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-gray-700 px-2">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D]/90">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-base sm:text-lg text-white/90 mb-8">
              Join thousands of satisfied customers who trust Taskbudy for their home services
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/auth/signup')}
                className="w-full sm:w-auto px-8 py-4 bg-[#FF6A00] text-white rounded-lg hover:bg-[#E55F00] transition-all duration-300 font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer transform hover:scale-105"
              >
                Sign Up Now
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="w-full sm:w-auto px-8 py-4 bg-white/20 backdrop-blur-sm text-white border-2 border-white rounded-lg hover:bg-white/30 transition-all duration-300 font-semibold text-base sm:text-lg whitespace-nowrap cursor-pointer transform hover:scale-105"
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B3C5D] mb-12 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
              {[
                {
                  question: 'How does Taskbudy work?',
                  answer: 'Simply describe the problem, choose whether you need help ASAP or at a later date, and we\'ll match you with a trusted local professional. You\'ll see the price before booking and can track the job from acceptance to arrival.',
                  color: 'bg-[#0B3C5D]'
                },
                {
                  question: 'What services can I book?',
                  answer: 'With Taskbudy, you can book handyman services, plumbing, electrical work, cleaning, rubbish & waste removal, and transportation & deliveries. Availability may vary by area and time.',
                  color: 'bg-[#FF6A00]'
                },
                {
                  question: 'Can I get same-day or emergency service?',
                  answer: 'Yes. Same-day and emergency bookings are available depending on local availability. Many customers are matched with a professional within the hour.',
                  color: 'bg-[#0B3C5D]'
                },
                {
                  question: 'How much will it cost?',
                  answer: 'Pricing depends on the service, location, and job type. You\'ll always see clear pricing before confirming your booking — no hidden fees or surprise charges.',
                  color: 'bg-[#FF6A00]'
                },
                {
                  question: 'Do I need to get quotes?',
                  answer: 'No. Taskbudy removes the hassle of quotes and bidding. Job details and pricing are shown upfront so you can book quickly with confidence.',
                  color: 'bg-[#0B3C5D]'
                },
                {
                  question: 'Are Taskbudy professionals verified?',
                  answer: 'Yes. All professionals are ID-verified, background-checked, and insured. Providers are also rated after every job to maintain quality standards.',
                  color: 'bg-[#FF6A00]'
                },
                {
                  question: 'Can I choose a later date instead of ASAP?',
                  answer: 'Yes. You can book immediately or schedule a service for a date and time that suits you, subject to availability.',
                  color: 'bg-[#0B3C5D]'
                },
                {
                  question: 'What areas do you cover?',
                  answer: 'Taskbudy operates across multiple UK cities. Enter your postcode during booking to confirm coverage and see services available in your area.',
                  color: 'bg-[#FF6A00]'
                },
                {
                  question: 'What if I\'m not satisfied with the service?',
                  answer: 'We stand behind our work. If you\'re not satisfied, our support team will help resolve the issue in line with our quality guarantee.',
                  color: 'bg-[#0B3C5D]'
                },
                {
                  question: 'Is support available if I need help?',
                  answer: 'Yes. Our customer support team is available to assist you with bookings, questions, or any issues before, during, or after your service.',
                  color: 'bg-[#FF6A00]'
                }
              ].map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-[#FF6A00] hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 ${faq.color} rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <i className="ri-question-line text-white text-xl"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-[#0B3C5D] mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* CTA after FAQ */}
            <div className="text-center mt-12">
              <p className="text-lg text-gray-600 mb-6">
                Still have questions?
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="inline-block px-8 py-4 bg-[#FF6A00] text-white rounded-lg hover:bg-[#E55F00] transition-colors text-lg font-medium whitespace-nowrap cursor-pointer transform hover:scale-105"
              >
                Contact Our Team
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Back to Home Link - Above Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-all group mx-auto"
        >
          <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
          <span className="font-medium text-lg">Back to Home</span>
        </button>
      </div>

      {/* Footer */}
      <Footer />

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-[fadeIn_0.3s_ease-out]">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8 animate-[zoomIn_0.3s_ease-out]">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#0B3C5D]/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <i className="ri-lock-line text-[#0B3C5D] text-3xl"></i>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#0B3C5D] mb-2">Sign In Required</h2>
              <p className="text-sm sm:text-base text-gray-600">Please sign in or create an account to book a service</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/auth/signin')}
                className="w-full py-3 bg-[#0B3C5D] text-white rounded-lg hover:bg-[#082A42] transition-all duration-300 font-semibold cursor-pointer whitespace-nowrap transform hover:scale-105 hover:shadow-lg"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/auth/signup')}
                className="w-full py-3 bg-white text-[#FF6A00] border-2 border-[#FF6A00] rounded-lg hover:bg-[#FF6A00]/10 transition-all duration-300 font-semibold cursor-pointer whitespace-nowrap transform hover:scale-105"
              >
                Create Account
              </button>
              <button
                onClick={() => {
                  setShowAuthPrompt(false);
                  setSelectedService(null);
                }}
                className="w-full py-3 text-gray-600 hover:text-gray-900 transition-colors font-medium cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ServiceModal 
        service={selectedService} 
        onClose={() => setSelectedService(null)} 
      />
    </div>
  );
}
